import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MdSearch, MdAdd, MdEdit, MdDelete, MdVisibility, MdPeople,
  MdTrendingUp, MdRefresh, MdInfo, MdArrowForward,
  MdArrowUpward, MdArrowDownward, MdFileDownload, MdFilterList, MdClose,
  MdAssignmentInd, MdSchedule, MdUpload, MdSupervisorAccount
} from 'react-icons/md';
import SkeletonRow from '../components/SkeletonRow';
import BulkUpload from '../components/BulkUpload';
import { candidateApi, jobRequisitionApi, usersApi, jobMasterApi, jobBulkUploadApi } from '../../../api/jobManagementApi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './CandidatePipeline.css';

// MUST match backend CandidateStage enum exactly
const CANDIDATE_STAGES = [
  { value: 'SOURCED', label: 'Sourced', color: 'sourced' },
  { value: 'SCREENING', label: 'Screening', color: 'screening' },
  { value: 'INTERVIEW_SCHEDULED', label: 'Interview Scheduled', color: 'interview' },
  { value: 'INTERVIEW_IN_PROGRESS', label: 'Interview In Progress', color: 'interview' },
  { value: 'OPS_INTERVIEW', label: 'Ops Interview', color: 'interview' },
  { value: 'SELECTED', label: 'Selected', color: 'selected' },
  { value: 'OFFER_PENDING', label: 'Offer Pending', color: 'offered' },
  { value: 'OFFER_RELEASED', label: 'Offer Released', color: 'offered' },
  { value: 'OFFER_ACCEPTED', label: 'Offer Accepted', color: 'offered' },
  { value: 'DOCUMENTATION', label: 'Documentation', color: 'offered' },
  { value: 'BGV_INITIATED', label: 'BGV Initiated', color: 'offered' },
  { value: 'BGV_COMPLETED', label: 'BGV Completed', color: 'offered' },
  { value: 'JOINED', label: 'Joined', color: 'joined' },
  { value: 'REJECTED', label: 'Rejected', color: 'rejected' },
  { value: 'WITHDRAWN', label: 'Withdrawn', color: 'withdrawn' },
  { value: 'ON_HOLD', label: 'On Hold', color: 'screening' },
  { value: 'NO_SHOW', label: 'No Show', color: 'rejected' },
  { value: 'OFFER_DECLINED', label: 'Offer Declined', color: 'rejected' }
];

const SOURCE_TYPES = [
  { value: 'NAUKRI', label: 'Naukri' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'INDEED', label: 'Indeed' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'ER', label: 'ER (Employee Referral)' },
  { value: 'DIRECT', label: 'Direct' },
  { value: 'JOB_PORTAL', label: 'Job Portal' },
  { value: 'WALK_IN', label: 'Walk-in' },
  { value: 'CAMPUS', label: 'Campus' },
  { value: 'CONSULTANT', label: 'Consultant' },
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'JOB_FAIR', label: 'Job Fair' },
  { value: 'SOCIAL_MEDIA', label: 'Social Media' },
  { value: 'OTHER', label: 'Other' }
];

function CandidatePipeline() {
  const [searchParams] = useSearchParams();
  const requisitionId = searchParams.get('requisitionId');

  const [candidates, setCandidates] = useState([]);
  const [pipelineSummary, setPipelineSummary] = useState({});
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    query: '',
    stage: '',
    requisitionId: requisitionId || '',
    verticalId: '',
    sourceType: '',
    recruiterId: '',
    startDate: '',
    endDate: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 20;

  // Sorting
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Dropdowns
  const [requisitions, setRequisitions] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [verticals, setVerticals] = useState([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [stageCandidate, setStageCandidate] = useState(null);
  const [newStage, setNewStage] = useState('');
  const [stageRemarks, setStageRemarks] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewCandidate, setViewCandidate] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);

  // Interview Scheduling Modal
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [interviewCandidate, setInterviewCandidate] = useState(null);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewRemarks, setInterviewRemarks] = useState('');

  // Bulk Upload Modal
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Coordinator Assignments Modal (for HR Head / Super Admin)
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [coordinatorAssignments, setCoordinatorAssignments] = useState(null);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  // Form data - comprehensive fields
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    email: '',
    phone: '',
    alternateContact: '',
    address: '',
    // Job Info
    requisitionId: '',
    verticalName: '',
    processName: '',
    projectId: '',
    designationId: '',
    specialtyId: '',
    workLocationId: '',
    wfhWfo: '',
    // Pipeline & Source
    recruiterId: '',
    source: 'OTHER',
    subSource: '',
    currentStage: 'SOURCED',
    hireStatus: '',
    // Experience & Compensation
    experienceTypeStr: '',
    totalExperience: '',
    relevantExperience: '',
    currentCompany: '',
    currentCTC: '',
    expectedCTC: '',
    offeredCTC: '',
    noticePeriod: '',
    // Important Dates
    dateOfInterview: '',
    dateOfJoining: '',
    dateOfOffer: '',
    offerAcceptanceDate: '',
    // Interview Details
    assessmentScore: '',
    vaInterviewer: '',
    opsInterviewer: '',
    opsInterviewDate: '',
    opsInterviewStatus: '',
    // Management
    buLeadName: '',
    buHeadName: '',
    spocName: '',
    // System & Verification
    hbPbSystem: '',
    hostSystem: '',
    uanCheckStatus: '',
    uanDate: '',
    dojConfCurrent: '',
    roHire: '',
    // Other
    evergreenRequisition: '',
    evergreenReqId: '',
    cgmAria: '',
    remarks: ''
  });

  // Load data
  const loadCandidates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const params = {
        page,
        size: pageSize,
        sortBy,
        sortDir
      };

      if (filters.query) params.query = filters.query;
      if (filters.stage) params.stage = filters.stage;
      if (filters.requisitionId) params.requisitionId = filters.requisitionId;
      if (filters.verticalId) params.verticalId = filters.verticalId;
      if (filters.sourceType) params.sourceType = filters.sourceType;
      if (filters.recruiterId) params.recruiterId = filters.recruiterId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await candidateApi.getAll(params);

      if (response.success) {
        setCandidates(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load candidates');
    } finally {
      setIsLoading(false);
    }
  }, [page, filters, sortBy, sortDir]);

  // Export filtered data
  const handleExport = async () => {
    try {
      // Get all data with current filters (no pagination limit)
      const params = { ...filters, page: 0, size: 10000 };
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
      const response = await candidateApi.getAll(params);

      if (response.success && response.data.content) {
        const data = response.data.content;

        // Create CSV content
        const headers = ['Candidate Code', 'Name', 'Email', 'Phone', 'JR Code', 'Source', 'Experience (Yrs)', 'Stage', 'Interview Date', 'Recruiter', 'Current CTC', 'Expected CTC'];
        const csvContent = [
          headers.join(','),
          ...data.map(c => [
            c.candidateCode || '',
            `"${(c.fullName || c.firstName || '').replace(/"/g, '""')}"`,
            c.email || '',
            c.contactNo || '',
            c.jobRequisition?.requisitionCode || '',
            c.sourceType || '',
            c.totalExperienceMonths ? (c.totalExperienceMonths / 12).toFixed(1) : '',
            c.currentStage || '',
            c.dateOfInterview ? new Date(c.dateOfInterview).toLocaleDateString() : '',
            c.recruiterName || '',
            c.currentCtc || '',
            c.expectedCtc || ''
          ].join(','))
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `candidates_pipeline_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        toast.success(`Exported ${data.length} candidates`);
      }
    } catch (e) {
      toast.error('Failed to export data');
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      query: '',
      stage: '',
      requisitionId: '',
      verticalId: '',
      sourceType: '',
      recruiterId: '',
      startDate: '',
      endDate: ''
    });
    setPage(0);
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortDir === 'asc' ? <MdArrowUpward className="sort-icon" /> : <MdArrowDownward className="sort-icon" />;
  };

  const loadPipelineSummary = async () => {
    try {
      const response = await candidateApi.getPipelineSummary(filters.requisitionId || null);
      if (response.success) {
        setPipelineSummary(response.data);
      }
    } catch (e) {
      console.error('Failed to load pipeline summary:', e);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [requisitionsRes, recruitersRes, verticalsRes] = await Promise.all([
        jobRequisitionApi.getOpenRequisitions(),
        usersApi.getRecruiters(), // Only users with recruiter/HR roles
        jobMasterApi.getVerticalsDropdown()
      ]);

      setRequisitions(requisitionsRes.success ? requisitionsRes.data : []);
      setRecruiters(Array.isArray(recruitersRes?.data) ? recruitersRes.data : []);
      setVerticals(verticalsRes.success ? verticalsRes.data : []);
    } catch (e) {
      console.error('Failed to load dropdowns:', e);
    }
  };

  const loadRequisitionDetails = async (id) => {
    if (!id) {
      setSelectedRequisition(null);
      return;
    }
    try {
      const response = await jobRequisitionApi.getById(id);
      if (response.success) {
        setSelectedRequisition(response.data);
      }
    } catch (e) {
      console.error('Failed to load requisition details:', e);
    }
  };

  useEffect(() => {
    loadCandidates();
    loadPipelineSummary();
    loadDropdowns();
  }, [loadCandidates]);

  useEffect(() => {
    if (filters.requisitionId) {
      loadRequisitionDetails(filters.requisitionId);
    } else {
      setSelectedRequisition(null);
    }
  }, [filters.requisitionId]);

  // Handlers
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingCandidate(null);
    setFormData({
      // Basic Info
      name: '',
      email: '',
      phone: '',
      alternateContact: '',
      address: '',
      // Job Info
      requisitionId: filters.requisitionId || '',
      verticalName: '',
      processName: '',
      projectId: '',
      designationId: '',
      specialtyId: '',
      workLocationId: '',
      wfhWfo: '',
      // Pipeline & Source
      recruiterId: '',
      source: 'OTHER',
      subSource: '',
      currentStage: 'SOURCED',
      hireStatus: '',
      // Experience & Compensation
      experienceTypeStr: '',
      totalExperience: '',
      relevantExperience: '',
      currentCompany: '',
      currentCTC: '',
      expectedCTC: '',
      offeredCTC: '',
      noticePeriod: '',
      // Important Dates
      dateOfInterview: '',
      dateOfJoining: '',
      dateOfOffer: '',
      offerAcceptanceDate: '',
      // Interview Details
      assessmentScore: '',
      vaInterviewer: '',
      opsInterviewer: '',
      opsInterviewDate: '',
      opsInterviewStatus: '',
      // Management
      buLeadName: '',
      buHeadName: '',
      spocName: '',
      // System & Verification
      hbPbSystem: '',
      hostSystem: '',
      uanCheckStatus: '',
      uanDate: '',
      dojConfCurrent: '',
      roHire: '',
      // Other
      evergreenRequisition: '',
      evergreenReqId: '',
      cgmAria: '',
      remarks: ''
    });
    setShowModal(true);
  };

  const openEditModal = (candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      // Basic Info
      name: candidate.fullName || candidate.firstName || '',
      email: candidate.email || '',
      phone: candidate.contactNo || '',
      alternateContact: candidate.alternateContact || '',
      address: candidate.address || '',
      // Job Info
      requisitionId: candidate.jobRequisition?.id || '',
      verticalName: candidate.verticalName || '',
      processName: candidate.processName || '',
      projectId: candidate.project?.id || '',
      designationId: candidate.designation?.id || '',
      specialtyId: candidate.specialty?.id || '',
      workLocationId: candidate.workLocation?.id || '',
      wfhWfo: candidate.wfhWfo || '',
      // Pipeline & Source
      recruiterId: candidate.recruiterId || '',
      source: candidate.sourceType || 'OTHER',
      subSource: candidate.subSource || '',
      currentStage: candidate.currentStage || 'SOURCED',
      hireStatus: candidate.hireStatus || '',
      // Experience & Compensation
      experienceTypeStr: candidate.experienceTypeStr || '',
      totalExperience: candidate.totalExperienceMonths ? (candidate.totalExperienceMonths / 12).toFixed(1) : '',
      relevantExperience: candidate.relevantExperienceMonths ? (candidate.relevantExperienceMonths / 12).toFixed(1) : '',
      currentCompany: candidate.lastCompany || '',
      currentCTC: candidate.currentCtc || '',
      expectedCTC: candidate.expectedCtc || '',
      offeredCTC: candidate.offeredCtc || '',
      noticePeriod: candidate.noticePeriodDays || '',
      // Important Dates
      dateOfInterview: candidate.dateOfInterview ? candidate.dateOfInterview.split('T')[0] : '',
      dateOfJoining: candidate.dateOfJoining ? candidate.dateOfJoining.split('T')[0] : '',
      dateOfOffer: candidate.dateOfOffer ? candidate.dateOfOffer.split('T')[0] : '',
      offerAcceptanceDate: candidate.offerAcceptanceDate ? candidate.offerAcceptanceDate.split('T')[0] : '',
      // Interview Details
      assessmentScore: candidate.assessmentScore || '',
      vaInterviewer: candidate.vaInterviewer || '',
      opsInterviewer: candidate.opsInterviewer || '',
      opsInterviewDate: candidate.opsInterviewDate ? candidate.opsInterviewDate.split('T')[0] : '',
      opsInterviewStatus: candidate.opsInterviewStatus || '',
      // Management
      buLeadName: candidate.buLeadName || '',
      buHeadName: candidate.buHeadName || '',
      spocName: candidate.spocName || '',
      // System & Verification
      hbPbSystem: candidate.hbPbSystem || '',
      hostSystem: candidate.hostSystem || '',
      uanCheckStatus: candidate.uanCheckStatus || '',
      uanDate: candidate.uanDate ? candidate.uanDate.split('T')[0] : '',
      dojConfCurrent: candidate.dojConfCurrent || '',
      roHire: candidate.roHire || '',
      // Other
      evergreenRequisition: candidate.evergreenRequisition || '',
      evergreenReqId: candidate.evergreenReqId || '',
      cgmAria: candidate.cgmAria || '',
      remarks: candidate.remarks || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCandidate(null);
    setError('');
  };

  const openStageModal = (candidate) => {
    setStageCandidate(candidate);
    setNewStage(candidate.currentStage || 'SOURCED');
    setStageRemarks('');
    setShowStageModal(true);
  };

  const closeStageModal = () => {
    setShowStageModal(false);
    setStageCandidate(null);
    setNewStage('');
    setStageRemarks('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Check if stage is being changed TO INTERVIEW_SCHEDULED
      // If so, we need to show the interview scheduling modal instead
      const isNewInterviewScheduled = formData.currentStage === 'INTERVIEW_SCHEDULED' &&
        editingCandidate &&
        editingCandidate.currentStage !== 'INTERVIEW_SCHEDULED';

      if (isNewInterviewScheduled) {
        // Close edit modal and open interview scheduling modal
        setInterviewCandidate(editingCandidate);
        setInterviewDate(formData.dateOfInterview || '');
        setInterviewTime('');
        setInterviewRemarks(formData.remarks || '');
        setShowInterviewModal(true);
        closeModal();
        setIsSubmitting(false);
        return;
      }

      // Parse name into first/last name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const payload = {
        // Basic Info
        firstName: firstName,
        lastName: lastName,
        fullName: formData.name,
        email: formData.email,
        contactNo: formData.phone,
        alternateContact: formData.alternateContact || null,
        address: formData.address || null,
        // Job Info
        jobRequisition: formData.requisitionId ? { id: formData.requisitionId } : null,
        verticalName: formData.verticalName || null,
        processName: formData.processName || null,
        project: formData.projectId ? { id: formData.projectId } : null,
        designation: formData.designationId ? { id: formData.designationId } : null,
        specialty: formData.specialtyId ? { id: formData.specialtyId } : null,
        workLocation: formData.workLocationId ? { id: formData.workLocationId } : null,
        wfhWfo: formData.wfhWfo || null,
        // Pipeline & Source
        recruiterId: formData.recruiterId || null,
        recruiterName: formData.recruiterId ? recruiters.find(r => r.id === formData.recruiterId)?.name : null,
        sourceType: formData.source,
        subSource: formData.subSource || null,
        currentStage: formData.currentStage,
        hireStatus: formData.hireStatus || null,
        // Experience & Compensation
        experienceTypeStr: formData.experienceTypeStr || null,
        lastCompany: formData.currentCompany,
        totalExperienceMonths: formData.totalExperience ? Math.round(parseFloat(formData.totalExperience) * 12) : null,
        relevantExperienceMonths: formData.relevantExperience ? Math.round(parseFloat(formData.relevantExperience) * 12) : null,
        currentCtc: formData.currentCTC ? parseFloat(formData.currentCTC) : null,
        expectedCtc: formData.expectedCTC ? parseFloat(formData.expectedCTC) : null,
        offeredCtc: formData.offeredCTC ? parseFloat(formData.offeredCTC) : null,
        noticePeriodDays: formData.noticePeriod ? parseInt(formData.noticePeriod) : null,
        // Important Dates
        dateOfInterview: formData.dateOfInterview || null,
        dateOfJoining: formData.dateOfJoining || null,
        dateOfOffer: formData.dateOfOffer || null,
        offerAcceptanceDate: formData.offerAcceptanceDate || null,
        // Interview Details
        assessmentScore: formData.assessmentScore || null,
        vaInterviewer: formData.vaInterviewer || null,
        opsInterviewer: formData.opsInterviewer || null,
        opsInterviewDate: formData.opsInterviewDate || null,
        opsInterviewStatus: formData.opsInterviewStatus || null,
        // Management
        buLeadName: formData.buLeadName || null,
        buHeadName: formData.buHeadName || null,
        spocName: formData.spocName || null,
        // System & Verification
        hbPbSystem: formData.hbPbSystem || null,
        hostSystem: formData.hostSystem || null,
        uanCheckStatus: formData.uanCheckStatus || null,
        uanDate: formData.uanDate || null,
        dojConfCurrent: formData.dojConfCurrent || null,
        roHire: formData.roHire || null,
        // Other
        evergreenRequisition: formData.evergreenRequisition || null,
        evergreenReqId: formData.evergreenReqId || null,
        cgmAria: formData.cgmAria || null,
        remarks: formData.remarks
      };

      let response;
      if (editingCandidate) {
        response = await candidateApi.update(editingCandidate.id, payload);
        toast.success('Candidate updated successfully');
      } else {
        response = await candidateApi.create(payload);
        toast.success('Candidate added successfully');
      }

      if (response.success) {
        await loadCandidates();
        await loadPipelineSummary();
        closeModal();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save candidate';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStageUpdate = async () => {
    if (!stageCandidate || !newStage) {
      return;
    }

    // If changing TO INTERVIEW_SCHEDULED, show interview scheduling modal
    if (newStage === 'INTERVIEW_SCHEDULED' && stageCandidate.currentStage !== 'INTERVIEW_SCHEDULED') {
      setInterviewCandidate(stageCandidate);
      setInterviewDate(stageCandidate.dateOfInterview || '');
      setInterviewTime(stageCandidate.interviewTime || '');
      setInterviewRemarks(stageRemarks);
      setShowInterviewModal(true);
      closeStageModal();
      return;
    }

    // If already at INTERVIEW_SCHEDULED and selecting it again, also open interview modal
    if (newStage === 'INTERVIEW_SCHEDULED' && stageCandidate.currentStage === 'INTERVIEW_SCHEDULED') {
      // Already at INTERVIEW_SCHEDULED, open scheduling modal for coordinator assignment
      setInterviewCandidate(stageCandidate);
      setInterviewDate(stageCandidate.dateOfInterview ? stageCandidate.dateOfInterview.split('T')[0] : '');
      setInterviewTime(stageCandidate.interviewTime || '');
      setInterviewRemarks(stageRemarks);
      setShowInterviewModal(true);
      closeStageModal();
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await candidateApi.updateStage(stageCandidate.id, newStage, stageRemarks);

      if (result.success) {
        toast.success(`Stage updated to ${newStage}`);
        await loadCandidates();
        await loadPipelineSummary();
        closeStageModal();
      } else {
        toast.error(result.message || 'Failed to update stage');
      }
    } catch (e) {
      console.error('Stage update error:', e);
      toast.error(e.response?.data?.message || 'Failed to update stage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewCandidate) return;

    if (!interviewDate) {
      toast.error('Please select interview date');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await candidateApi.scheduleInterview(
        interviewCandidate.id,
        interviewDate,
        interviewTime,
        interviewRemarks
      );

      // Check if the operation was successful (backend returns success: false for errors like no coordinators)
      if (result.success === false) {
        toast.error(result.error || result.message || 'Failed to schedule interview. Please check if coordinators are available.');
        return;
      }

      toast.success(result.message || 'Interview scheduled successfully! Coordinator assigned and notified.');
      await loadCandidates();
      await loadPipelineSummary();
      closeInterviewModal();
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.message || 'Failed to schedule interview');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeInterviewModal = () => {
    setShowInterviewModal(false);
    setInterviewCandidate(null);
    setInterviewDate('');
    setInterviewTime('');
    setInterviewRemarks('');
  };

  // Open interview scheduling modal for candidates already at INTERVIEW_SCHEDULED
  const openInterviewSchedulingModal = (candidate) => {
    setInterviewCandidate(candidate);
    setInterviewDate(candidate.dateOfInterview ? candidate.dateOfInterview.split('T')[0] : '');
    setInterviewTime(candidate.interviewTime || '');
    setInterviewRemarks('');
    setShowInterviewModal(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await candidateApi.delete(deleteConfirm.id);
      toast.success('Candidate deleted successfully');
      await loadCandidates();
      await loadPipelineSummary();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete candidate');
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Selection handlers
  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === candidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(candidates.map(c => c.id));
    }
  };

  // Bulk assign to coordinators
  const handleBulkAssignCoordinators = async () => {
    if (selectedCandidates.length === 0) {
      toast.warning('Please select candidates to assign');
      return;
    }

    if (!window.confirm(`Are you sure you want to assign ${selectedCandidates.length} candidate(s) to coordinators? They will be randomly assigned based on workload.`)) {
      return;
    }

    setIsAssigning(true);
    try {
      const response = await candidateApi.bulkAssignCoordinators(selectedCandidates);
      if (response.success) {
        const data = response.data;
        toast.success(`Successfully assigned ${data.successCount} candidate(s) to ${data.coordinatorsAssigned} coordinator(s). Email will be sent when interview is scheduled.`);
        if (data.failedCount > 0) {
          toast.warning(`${data.failedCount} assignment(s) failed`);
        }
        setSelectedCandidates([]);
        await loadCandidates();
      } else {
        toast.error(response.error || response.message || 'Assignment failed. Please check if coordinators are available.');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.message || 'Failed to assign coordinators');
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle bulk upload - explicitly process as CANDIDATE data
  const handleBulkUpload = async (_data, file) => {
    const response = await jobBulkUploadApi.uploadExcel(file, 'CANDIDATE');
    if (response.success) {
      await loadCandidates();
      await loadPipelineSummary();
    }
    return response;
  };

  // Load coordinator assignments (for HR Head / Super Admin visibility)
  const loadCoordinatorAssignments = async () => {
    setIsLoadingAssignments(true);
    try {
      const response = await candidateApi.getCoordinatorAssignmentSummary();
      if (response.success) {
        setCoordinatorAssignments(response.data);
      } else {
        toast.error('Failed to load coordinator assignments');
      }
    } catch (e) {
      toast.error('Failed to load coordinator assignments');
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  const openAssignmentsModal = () => {
    setShowAssignmentsModal(true);
    loadCoordinatorAssignments();
  };

  const getStageClass = (stage) => {
    const stageConfig = CANDIDATE_STAGES.find(s => s.value === stage);
    return stageConfig?.color || 'sourced';
  };

  const getStageLabel = (stage) => {
    const stageConfig = CANDIDATE_STAGES.find(s => s.value === stage);
    return stageConfig?.label || stage;
  };

  return (
    <div className="admin-view candidate-pipeline-view">
      {/* Compact Header with Filters - At Top */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdPeople className="title-icon" />
            Candidate Pipeline
          </h2>
        </div>
        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search name, email..."
              value={filters.query}
              onChange={(e) => handleFilterChange('query', e.target.value)}
            />
          </div>
          <select
            className="filter-select-compact"
            value={filters.requisitionId}
            onChange={(e) => handleFilterChange('requisitionId', e.target.value)}
          >
            <option value="">All JRs</option>
            {requisitions.map(r => (
              <option key={r.id} value={r.id}>{r.requisitionCode} - {r.processName}</option>
            ))}
          </select>
          <select
            className="filter-select-compact"
            value={filters.stage}
            onChange={(e) => handleFilterChange('stage', e.target.value)}
          >
            <option value="">All Stages</option>
            {CANDIDATE_STAGES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button
            className={`btn-book-compact ${showAdvancedFilters ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            title="Advanced Filters"
          >
            <MdFilterList />
            {activeFilterCount > 3 && <span className="filter-badge">{activeFilterCount - 3}</span>}
          </button>
          <button className="btn-book-compact" onClick={handleExport} title="Export to CSV">
            <MdFileDownload />
          </button>
          <button className="btn-book-compact" onClick={() => { loadCandidates(); loadPipelineSummary(); }} title="Refresh">
            <MdRefresh />
          </button>
          {selectedCandidates.length > 0 && (
            <button
              className="btn-book-compact"
              onClick={handleBulkAssignCoordinators}
              disabled={isAssigning}
              title={`Assign ${selectedCandidates.length} candidate(s) to coordinators`}
              style={{ background: '#7c3aed', color: 'white' }}
            >
              <MdAssignmentInd />
              <span>{isAssigning ? 'Assigning...' : `Assign (${selectedCandidates.length})`}</span>
            </button>
          )}
          <button
            className="btn-book-compact"
            onClick={() => setShowBulkUploadModal(true)}
            title="Bulk upload candidates from CSV/Excel"
            style={{ background: '#217346', color: 'white' }}
          >
            <MdUpload />
            <span>Bulk Upload</span>
          </button>
          <button
            className="btn-book-compact"
            onClick={openAssignmentsModal}
            title="View coordinator assignments (HR Head / Admin)"
            style={{ background: '#6366f1', color: 'white' }}
          >
            <MdSupervisorAccount />
            <span>Assignments</span>
          </button>
          <button className="btn-book-compact" onClick={openAddModal}>
            <MdAdd />
            <span>Add Candidate</span>
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="advanced-filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Source</label>
              <select
                value={filters.sourceType}
                onChange={(e) => handleFilterChange('sourceType', e.target.value)}
              >
                <option value="">All Sources</option>
                {SOURCE_TYPES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Recruiter</label>
              <select
                value={filters.recruiterId}
                onChange={(e) => handleFilterChange('recruiterId', e.target.value)}
              >
                <option value="">All Recruiters</option>
                {recruiters.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Vertical</label>
              <select
                value={filters.verticalId}
                onChange={(e) => handleFilterChange('verticalId', e.target.value)}
              >
                <option value="">All Verticals</option>
                {verticals.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>From Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>To Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn-clear-filters" onClick={clearFilters}>
              <MdClose /> Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Selected Requisition Info Banner */}
      {selectedRequisition && (
        <div className="info-banner">
          <MdInfo className="info-icon" />
          <div className="info-text">
            Viewing candidates for <strong>{selectedRequisition.requisitionCode}</strong> - {selectedRequisition.processName}
            {' '}({selectedRequisition.positionsFilled || 0}/{selectedRequisition.positionsApproved || 0} filled)
          </div>
        </div>
      )}

      {/* Pipeline Stats */}
      <div className="pipeline-stats">
        <div className="pipeline-stat">
          <span className="stat-dot dot-sourced"></span>
          <span className="stat-count">{pipelineSummary.SOURCED || 0}</span>
          <span className="stat-label">Sourced</span>
        </div>
        <div className="pipeline-stat">
          <span className="stat-dot dot-screening"></span>
          <span className="stat-count">{pipelineSummary.SCREENING || 0}</span>
          <span className="stat-label">Screening</span>
        </div>
        <div className="pipeline-stat">
          <span className="stat-dot dot-interview"></span>
          <span className="stat-count">{(pipelineSummary.INTERVIEW_SCHEDULED || 0) + (pipelineSummary.INTERVIEW_COMPLETED || 0)}</span>
          <span className="stat-label">Interview</span>
        </div>
        <div className="pipeline-stat">
          <span className="stat-dot dot-selected"></span>
          <span className="stat-count">{pipelineSummary.SELECTED || 0}</span>
          <span className="stat-label">Selected</span>
        </div>
        <div className="pipeline-stat">
          <span className="stat-dot dot-offered"></span>
          <span className="stat-count">{(pipelineSummary.OFFER_RELEASED || 0) + (pipelineSummary.OFFER_ACCEPTED || 0)}</span>
          <span className="stat-label">Offered</span>
        </div>
        <div className="pipeline-stat">
          <span className="stat-dot dot-joined"></span>
          <span className="stat-count">{pipelineSummary.JOINED || 0}</span>
          <span className="stat-label">Joined</span>
        </div>
        <div className="pipeline-stat">
          <span className="stat-dot dot-rejected"></span>
          <span className="stat-count">{(pipelineSummary.REJECTED || 0) + (pipelineSummary.WITHDRAWN || 0)}</span>
          <span className="stat-label">Dropped</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Table */}
      <div className="table-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading candidates...</p>
          </div>
        ) : candidates.length === 0 ? (
          <div className="empty-state">
            <h3>No Candidates Found</h3>
            <p>Add candidates to the pipeline to get started</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedCandidates.length === candidates.length && candidates.length > 0}
                    onChange={handleSelectAll}
                    title="Select all"
                  />
                </th>
                <th>S.No</th>
                <th className="sortable" onClick={() => handleSort('candidateCode')}>
                  Code {renderSortIcon('candidateCode')}
                </th>
                <th className="sortable" onClick={() => handleSort('fullName')}>
                  Name {renderSortIcon('fullName')}
                </th>
                <th className="sortable" onClick={() => handleSort('email')}>
                  Email {renderSortIcon('email')}
                </th>
                <th>Phone</th>
                <th>JR Code</th>
                <th className="sortable" onClick={() => handleSort('sourceType')}>
                  Source {renderSortIcon('sourceType')}
                </th>
                <th className="sortable" onClick={() => handleSort('totalExperienceMonths')}>
                  Exp {renderSortIcon('totalExperienceMonths')}
                </th>
                <th className="sortable" onClick={() => handleSort('currentStage')}>
                  Stage {renderSortIcon('currentStage')}
                </th>
                <th className="sortable" onClick={() => handleSort('dateOfInterview')}>
                  DOI {renderSortIcon('dateOfInterview')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate, index) => (
                <tr key={candidate.id} className={selectedCandidates.includes(candidate.id) ? 'selected-row' : ''}>
                  <td style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedCandidates.includes(candidate.id)}
                      onChange={() => handleSelectCandidate(candidate.id)}
                    />
                  </td>
                  <td className="sno-cell">{page * pageSize + index + 1}</td>
                  <td className="code-cell">{candidate.candidateCode || '-'}</td>
                  <td className="name-cell">{candidate.fullName || candidate.firstName || '-'}</td>
                  <td className="email-cell">{candidate.email || '-'}</td>
                  <td className="phone-cell">{candidate.contactNo || '-'}</td>
                  <td className="jr-code-cell">{candidate.jobRequisition?.requisitionCode || '-'}</td>
                  <td>{candidate.sourceType || '-'}</td>
                  <td className="exp-cell">
                    {candidate.totalExperienceMonths ? `${(candidate.totalExperienceMonths / 12).toFixed(1)} yrs` : '-'}
                  </td>
                  <td>
                    <span className={`stage-badge ${getStageClass(candidate.currentStage)}`}>
                      {getStageLabel(candidate.currentStage)}
                    </span>
                  </td>
                  <td className="date-cell">
                    {candidate.dateOfInterview ? new Date(candidate.dateOfInterview).toLocaleDateString() : '-'}
                  </td>
                  <td className="actions-cell">
                    <button className="btn-action-compact" onClick={() => { setViewCandidate(candidate); setShowViewModal(true); }} title="View Details">
                      <MdVisibility />
                    </button>
                    <button className="btn-action-compact" onClick={() => openEditModal(candidate)} title="Edit">
                      <MdEdit />
                    </button>
                    <button className="btn-action-compact" onClick={() => openStageModal(candidate)} title="Update Stage">
                      <MdArrowForward />
                    </button>
                    {candidate.currentStage === 'INTERVIEW_SCHEDULED' && !candidate.coordinatorId && (
                      <button
                        className="btn-action-compact"
                        onClick={() => openInterviewSchedulingModal(candidate)}
                        title="Schedule Interview & Assign Coordinator"
                        style={{ color: '#7c3aed' }}
                      >
                        <MdSchedule />
                      </button>
                    )}
                    <button className="btn-action-compact btn-delete" onClick={() => setDeleteConfirm(candidate)} title="Delete">
                      <MdDelete />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalElements)} of {totalElements}
          </div>
          <div className="pagination-controls">
            <button className="pagination-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
              const pageNum = Math.max(0, Math.min(page - 2, totalPages - 5)) + idx;
              if (pageNum >= totalPages) return null;
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${page === pageNum ? 'active' : ''}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button className="pagination-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>{editingCandidate ? 'Edit Candidate' : 'Add Candidate'}</h3>
              <button className="modal-close" onClick={closeModal}>x</button>
            </div>

            <form onSubmit={handleSubmit} className="user-form edit-form-sections">
              {/* Basic Information */}
              <div className="form-section">
                <h4 className="form-section-title">Basic Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="alternateContact">Alternate Contact</label>
                    <input type="tel" id="alternateContact" name="alternateContact" value={formData.alternateContact} onChange={handleInputChange} />
                  </div>
                  <div className="form-group" style={{ flex: '2' }}>
                    <label htmlFor="address">Address</label>
                    <input type="text" id="address" name="address" value={formData.address} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* Job & Requisition */}
              <div className="form-section">
                <h4 className="form-section-title">Job & Requisition</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="requisitionId">Job Requisition *</label>
                    <select id="requisitionId" name="requisitionId" value={formData.requisitionId} onChange={handleInputChange} required>
                      <option value="">Select JR</option>
                      {requisitions.map(r => (
                        <option key={r.id} value={r.id}>{r.requisitionCode} - {r.processName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="verticalName">Vertical</label>
                    <input type="text" id="verticalName" name="verticalName" value={formData.verticalName} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="processName">Process</label>
                    <input type="text" id="processName" name="processName" value={formData.processName} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="wfhWfo">WFH/WFO</label>
                    <select id="wfhWfo" name="wfhWfo" value={formData.wfhWfo} onChange={handleInputChange}>
                      <option value="">Select</option>
                      <option value="WFH">WFH (Work From Home)</option>
                      <option value="WFO">WFO (Work From Office)</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Pipeline & Source */}
              <div className="form-section">
                <h4 className="form-section-title">Pipeline & Source</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="source">Source *</label>
                    <select id="source" name="source" value={formData.source} onChange={handleInputChange} required>
                      {SOURCE_TYPES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="subSource">Sub Source</label>
                    <input type="text" id="subSource" name="subSource" value={formData.subSource} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="recruiterId">Recruiter</label>
                    <select id="recruiterId" name="recruiterId" value={formData.recruiterId} onChange={handleInputChange}>
                      <option value="">Select Recruiter</option>
                      {recruiters.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="currentStage">Current Stage</label>
                    <select id="currentStage" name="currentStage" value={formData.currentStage} onChange={handleInputChange}>
                      {CANDIDATE_STAGES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    {formData.currentStage === 'INTERVIEW_SCHEDULED' && editingCandidate?.currentStage !== 'INTERVIEW_SCHEDULED' && (
                      <div style={{ marginTop: '8px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', fontSize: '13px', color: '#166534' }}>
                        Saving will open interview scheduling to assign coordinator
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="hireStatus">Hire Status</label>
                    <input type="text" id="hireStatus" name="hireStatus" value={formData.hireStatus} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* Experience & Compensation */}
              <div className="form-section">
                <h4 className="form-section-title">Experience & Compensation</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="experienceTypeStr">Experience Type</label>
                    <select id="experienceTypeStr" name="experienceTypeStr" value={formData.experienceTypeStr} onChange={handleInputChange}>
                      <option value="">Select</option>
                      <option value="Fresher">Fresher</option>
                      <option value="Experienced">Experienced</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="totalExperience">Total Experience (Years)</label>
                    <input type="number" id="totalExperience" name="totalExperience" value={formData.totalExperience} onChange={handleInputChange} step="0.5" min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="relevantExperience">Relevant Experience (Years)</label>
                    <input type="number" id="relevantExperience" name="relevantExperience" value={formData.relevantExperience} onChange={handleInputChange} step="0.5" min="0" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="currentCompany">Current/Last Company</label>
                    <input type="text" id="currentCompany" name="currentCompany" value={formData.currentCompany} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="noticePeriod">Notice Period (Days)</label>
                    <input type="number" id="noticePeriod" name="noticePeriod" value={formData.noticePeriod} onChange={handleInputChange} min="0" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="currentCTC">Current CTC</label>
                    <input type="number" id="currentCTC" name="currentCTC" value={formData.currentCTC} onChange={handleInputChange} placeholder="e.g., 500000" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="expectedCTC">Expected CTC</label>
                    <input type="number" id="expectedCTC" name="expectedCTC" value={formData.expectedCTC} onChange={handleInputChange} placeholder="e.g., 700000" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="offeredCTC">Offered CTC</label>
                    <input type="number" id="offeredCTC" name="offeredCTC" value={formData.offeredCTC} onChange={handleInputChange} placeholder="e.g., 650000" />
                  </div>
                </div>
              </div>

              {/* Important Dates */}
              <div className="form-section">
                <h4 className="form-section-title">Important Dates</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="dateOfInterview">Date of Interview (DOI)</label>
                    <input type="date" id="dateOfInterview" name="dateOfInterview" value={formData.dateOfInterview} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="dateOfOffer">Date of Offer</label>
                    <input type="date" id="dateOfOffer" name="dateOfOffer" value={formData.dateOfOffer} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="offerAcceptanceDate">Offer Acceptance Date</label>
                    <input type="date" id="offerAcceptanceDate" name="offerAcceptanceDate" value={formData.offerAcceptanceDate} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="dateOfJoining">Date of Joining (DOJ)</label>
                    <input type="date" id="dateOfJoining" name="dateOfJoining" value={formData.dateOfJoining} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* Interview Details */}
              <div className="form-section">
                <h4 className="form-section-title">Interview Details</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="assessmentScore">Assessment Score</label>
                    <input type="text" id="assessmentScore" name="assessmentScore" value={formData.assessmentScore} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="vaInterviewer">V&A Interviewer</label>
                    <input type="text" id="vaInterviewer" name="vaInterviewer" value={formData.vaInterviewer} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="opsInterviewer">Ops Interviewer</label>
                    <input type="text" id="opsInterviewer" name="opsInterviewer" value={formData.opsInterviewer} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="opsInterviewDate">Ops Interview Date</label>
                    <input type="date" id="opsInterviewDate" name="opsInterviewDate" value={formData.opsInterviewDate} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="opsInterviewStatus">Ops Interview Status</label>
                    <input type="text" id="opsInterviewStatus" name="opsInterviewStatus" value={formData.opsInterviewStatus} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* Management */}
              <div className="form-section">
                <h4 className="form-section-title">Management</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="buLeadName">BU Lead</label>
                    <input type="text" id="buLeadName" name="buLeadName" value={formData.buLeadName} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="buHeadName">BU Head</label>
                    <input type="text" id="buHeadName" name="buHeadName" value={formData.buHeadName} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="spocName">SPOC</label>
                    <input type="text" id="spocName" name="spocName" value={formData.spocName} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* System & Verification */}
              <div className="form-section">
                <h4 className="form-section-title">System & Verification</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="hbPbSystem">HB/PB System</label>
                    <input type="text" id="hbPbSystem" name="hbPbSystem" value={formData.hbPbSystem} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="hostSystem">Host System</label>
                    <input type="text" id="hostSystem" name="hostSystem" value={formData.hostSystem} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="uanCheckStatus">UAN Check Status</label>
                    <input type="text" id="uanCheckStatus" name="uanCheckStatus" value={formData.uanCheckStatus} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="uanDate">UAN Date</label>
                    <input type="date" id="uanDate" name="uanDate" value={formData.uanDate} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="dojConfCurrent">DOJ Conf/Current</label>
                    <input type="text" id="dojConfCurrent" name="dojConfCurrent" value={formData.dojConfCurrent} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="roHire">Re-hire</label>
                    <select id="roHire" name="roHire" value={formData.roHire} onChange={handleInputChange}>
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Evergreen & Other */}
              <div className="form-section">
                <h4 className="form-section-title">Evergreen & Other</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="evergreenRequisition">Evergreen Requisition</label>
                    <input type="text" id="evergreenRequisition" name="evergreenRequisition" value={formData.evergreenRequisition} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="evergreenReqId">Evergreen Req ID</label>
                    <input type="text" id="evergreenReqId" name="evergreenReqId" value={formData.evergreenReqId} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="cgmAria">CGM Aria</label>
                    <input type="text" id="cgmAria" name="cgmAria" value={formData.cgmAria} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div className="form-section">
                <h4 className="form-section-title">Remarks</h4>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1 1 100%' }}>
                    <textarea id="remarks" name="remarks" value={formData.remarks} onChange={handleInputChange} rows="3" placeholder="Add any additional notes or remarks..." />
                  </div>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : (editingCandidate ? 'Update' : 'Add Candidate')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stage Update Modal */}
      {showStageModal && stageCandidate && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Update Stage</h3>
              <button className="modal-close" onClick={closeStageModal}>x</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem' }}>
                Move <strong>{stageCandidate.fullName || stageCandidate.firstName}</strong> to a new stage
              </p>
              <div className="form-group">
                <label htmlFor="newStage">New Stage *</label>
                <select id="newStage" value={newStage} onChange={(e) => setNewStage(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  {CANDIDATE_STAGES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label htmlFor="stageRemarks">Remarks</label>
                <textarea
                  id="stageRemarks"
                  value={stageRemarks}
                  onChange={(e) => setStageRemarks(e.target.value)}
                  rows="3"
                  placeholder="Add any notes about this stage change..."
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={closeStageModal}>Cancel</button>
              <button type="button" className="admin-btn admin-btn-primary" onClick={handleStageUpdate} disabled={isSubmitting}>
                {isSubmitting ? 'Updating...' : 'Update Stage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interview Scheduling Modal */}
      {showInterviewModal && interviewCandidate && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '550px' }}>
            <div className="modal-header">
              <h3>Schedule Interview</h3>
              <button className="modal-close" onClick={closeInterviewModal}>x</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px', marginBottom: '1rem' }}>
                <p style={{ margin: 0, color: '#166534', fontSize: '14px' }}>
                  A coordinator will be <strong>randomly assigned</strong> and notified via email with the interview details.
                </p>
              </div>
              <p style={{ marginBottom: '1rem' }}>
                Schedule interview for <strong>{interviewCandidate.fullName || interviewCandidate.firstName}</strong>
              </p>
              <div className="form-group">
                <label htmlFor="interviewDate">Interview Date *</label>
                <input
                  type="date"
                  id="interviewDate"
                  value={interviewDate}
                  onChange={(e) => setInterviewDate(e.target.value)}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label htmlFor="interviewTime">Interview Time</label>
                <input
                  type="text"
                  id="interviewTime"
                  value={interviewTime}
                  onChange={(e) => setInterviewTime(e.target.value)}
                  placeholder="e.g., 10:00 AM - 11:00 AM"
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label htmlFor="interviewRemarks">Remarks</label>
                <textarea
                  id="interviewRemarks"
                  value={interviewRemarks}
                  onChange={(e) => setInterviewRemarks(e.target.value)}
                  rows="3"
                  placeholder="Add any notes about this interview..."
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={closeInterviewModal}>Cancel</button>
              <button type="button" className="admin-btn admin-btn-primary" onClick={handleScheduleInterview} disabled={isSubmitting}>
                {isSubmitting ? 'Scheduling...' : 'Schedule & Assign Coordinator'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>x</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteConfirm.fullName || deleteConfirm.firstName}</strong>?</p>
            </div>
            <div className="form-actions">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button type="button" className="admin-btn admin-btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* View Candidate Details Modal */}
      {showViewModal && viewCandidate && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>Candidate Details - {viewCandidate.candidateCode}</h3>
              <button className="modal-close" onClick={() => { setShowViewModal(false); setViewCandidate(null); }}>x</button>
            </div>
            <div className="modal-body">
              {/* Basic Information */}
              <div className="detail-section">
                <h4 className="detail-section-title">Basic Information</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">Candidate Code:</span><span className="detail-value">{viewCandidate.candidateCode || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Full Name:</span><span className="detail-value">{viewCandidate.fullName || viewCandidate.firstName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Email:</span><span className="detail-value">{viewCandidate.email || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Contact No:</span><span className="detail-value">{viewCandidate.contactNo || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Alternate Contact:</span><span className="detail-value">{viewCandidate.alternateContact || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Address:</span><span className="detail-value">{viewCandidate.address || '-'}</span></div>
                </div>
              </div>

              {/* Job Information */}
              <div className="detail-section">
                <h4 className="detail-section-title">Job & Requisition</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">Job Requisition:</span><span className="detail-value">{viewCandidate.jobRequisition?.requisitionCode || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Vertical:</span><span className="detail-value">{viewCandidate.verticalName || viewCandidate.jobRequisition?.vertical?.verticalName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Process:</span><span className="detail-value">{viewCandidate.processName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Project:</span><span className="detail-value">{viewCandidate.project?.projectName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Designation:</span><span className="detail-value">{viewCandidate.designation?.designationName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Specialty:</span><span className="detail-value">{viewCandidate.specialty?.specialtyName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Work Location:</span><span className="detail-value">{viewCandidate.workLocation?.locationName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">WFH/WFO:</span><span className="detail-value">{viewCandidate.wfhWfo || '-'}</span></div>
                </div>
              </div>

              {/* Pipeline & Source */}
              <div className="detail-section">
                <h4 className="detail-section-title">Pipeline & Source</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">Current Stage:</span><span className="detail-value"><span className={`stage-badge ${getStageClass(viewCandidate.currentStage)}`}>{getStageLabel(viewCandidate.currentStage)}</span></span></div>
                  <div className="detail-item"><span className="detail-label">Source:</span><span className="detail-value">{viewCandidate.sourceType || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Sub Source:</span><span className="detail-value">{viewCandidate.subSource || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Recruiter:</span><span className="detail-value">{viewCandidate.recruiterName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Hire Status:</span><span className="detail-value">{viewCandidate.hireStatus || '-'}</span></div>
                </div>
              </div>

              {/* Experience & Compensation */}
              <div className="detail-section">
                <h4 className="detail-section-title">Experience & Compensation</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">Experience Type:</span><span className="detail-value">{viewCandidate.experienceTypeStr || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Total Experience:</span><span className="detail-value">{viewCandidate.totalExperienceMonths ? `${(viewCandidate.totalExperienceMonths / 12).toFixed(1)} years` : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Relevant Experience:</span><span className="detail-value">{viewCandidate.relevantExperienceMonths ? `${(viewCandidate.relevantExperienceMonths / 12).toFixed(1)} years` : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Last Company:</span><span className="detail-value">{viewCandidate.lastCompany || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Current CTC:</span><span className="detail-value">{viewCandidate.currentCtc ? `₹${viewCandidate.currentCtc.toLocaleString()}` : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Expected CTC:</span><span className="detail-value">{viewCandidate.expectedCtc ? `₹${viewCandidate.expectedCtc.toLocaleString()}` : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Offered CTC:</span><span className="detail-value">{viewCandidate.offeredCtc ? `₹${viewCandidate.offeredCtc.toLocaleString()}` : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Notice Period:</span><span className="detail-value">{viewCandidate.noticePeriodDays ? `${viewCandidate.noticePeriodDays} days` : '-'}</span></div>
                </div>
              </div>

              {/* Dates */}
              <div className="detail-section">
                <h4 className="detail-section-title">Important Dates</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">Date of Interview (DOI):</span><span className="detail-value">{viewCandidate.dateOfInterview ? new Date(viewCandidate.dateOfInterview).toLocaleDateString() : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Date of Joining (DOJ):</span><span className="detail-value">{viewCandidate.dateOfJoining ? new Date(viewCandidate.dateOfJoining).toLocaleDateString() : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Date of Offer:</span><span className="detail-value">{viewCandidate.dateOfOffer ? new Date(viewCandidate.dateOfOffer).toLocaleDateString() : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Offer Acceptance Date:</span><span className="detail-value">{viewCandidate.offerAcceptanceDate ? new Date(viewCandidate.offerAcceptanceDate).toLocaleDateString() : '-'}</span></div>
                </div>
              </div>

              {/* Interview Details */}
              <div className="detail-section">
                <h4 className="detail-section-title">Interview Details</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">Assessment Score:</span><span className="detail-value">{viewCandidate.assessmentScore || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">V&A Interviewer:</span><span className="detail-value">{viewCandidate.vaInterviewer || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Ops Interviewer:</span><span className="detail-value">{viewCandidate.opsInterviewer || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Ops Interview Date:</span><span className="detail-value">{viewCandidate.opsInterviewDate ? new Date(viewCandidate.opsInterviewDate).toLocaleDateString() : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Ops Interview Status:</span><span className="detail-value">{viewCandidate.opsInterviewStatus || '-'}</span></div>
                </div>
              </div>

              {/* Management */}
              <div className="detail-section">
                <h4 className="detail-section-title">Management</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">BU Lead:</span><span className="detail-value">{viewCandidate.buLeadName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">BU Head:</span><span className="detail-value">{viewCandidate.buHeadName || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">SPOC:</span><span className="detail-value">{viewCandidate.spocName || '-'}</span></div>
                </div>
              </div>

              {/* System & Verification */}
              <div className="detail-section">
                <h4 className="detail-section-title">System & Verification</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">HB/PB System:</span><span className="detail-value">{viewCandidate.hbPbSystem || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Host System:</span><span className="detail-value">{viewCandidate.hostSystem || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">UAN Check Status:</span><span className="detail-value">{viewCandidate.uanCheckStatus || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">UAN Date:</span><span className="detail-value">{viewCandidate.uanDate ? new Date(viewCandidate.uanDate).toLocaleDateString() : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">DOJ Conf/Current:</span><span className="detail-value">{viewCandidate.dojConfCurrent || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Re-hire:</span><span className="detail-value">{viewCandidate.roHire || '-'}</span></div>
                </div>
              </div>

              {/* Evergreen */}
              <div className="detail-section">
                <h4 className="detail-section-title">Evergreen & Other</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">Evergreen Requisition:</span><span className="detail-value">{viewCandidate.evergreenRequisition || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Evergreen Req ID:</span><span className="detail-value">{viewCandidate.evergreenReqId || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">CGM Aria:</span><span className="detail-value">{viewCandidate.cgmAria || '-'}</span></div>
                </div>
              </div>

              {/* Remarks */}
              {viewCandidate.remarks && (
                <div className="detail-section">
                  <h4 className="detail-section-title">Remarks</h4>
                  <p style={{ margin: 0, padding: '8px', background: '#f7fafc', borderRadius: '4px' }}>{viewCandidate.remarks}</p>
                </div>
              )}

              {/* Audit Information */}
              <div className="detail-section">
                <h4 className="detail-section-title">Audit Information</h4>
                <div className="detail-grid">
                  <div className="detail-item"><span className="detail-label">Created By:</span><span className="detail-value">{viewCandidate.createdBy || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Created At:</span><span className="detail-value">{viewCandidate.createdAt ? new Date(viewCandidate.createdAt).toLocaleString() : '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Updated By:</span><span className="detail-value">{viewCandidate.updatedBy || '-'}</span></div>
                  <div className="detail-item"><span className="detail-label">Updated At:</span><span className="detail-value">{viewCandidate.updatedAt ? new Date(viewCandidate.updatedAt).toLocaleString() : '-'}</span></div>
                </div>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => { setShowViewModal(false); setViewCandidate(null); }}>Close</button>
              <button type="button" className="admin-btn admin-btn-primary" onClick={() => { setShowViewModal(false); openEditModal(viewCandidate); }}>Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUpload
          title="Bulk Upload Candidates"
          entityName="Candidates"
          templateColumns={[
            // NON-MANDATORY
            { name: 'S.No', label: 'S.No', required: false, type: 'string', example: '1' },
            // MANDATORY
            { name: '#', label: '#', required: true, type: 'string', example: 'HIM' },
            { name: 'DOI', label: 'DOI', required: true, type: 'string', example: '15-01-2025' },
            { name: 'DOJ', label: 'DOJ', required: true, type: 'string', example: '20-01-2025' },
            { name: 'Candidate ID', label: 'Candidate ID', required: true, type: 'string', example: 'CND001' },
            // NON-MANDATORY
            { name: 'Status', label: 'Status', required: false, type: 'string', example: 'Active' },
            // MANDATORY
            { name: 'Candidate Name', label: 'Candidate Name', required: true, type: 'string', example: 'John Doe' },
            { name: 'Designation', label: 'Designation', required: true, type: 'string', example: 'Sr Executive' },
            { name: 'Fresher/Experience', label: 'Fresher/Experience', required: true, type: 'string', example: 'Experience' },
            { name: 'Process', label: 'Process', required: true, type: 'string', example: 'AR' },
            { name: 'Speciality', label: 'Speciality', required: true, type: 'string', example: 'AR Laterals' },
            { name: 'BU Lead', label: 'BU Lead', required: true, type: 'string', example: 'Rahul Sharma' },
            { name: 'BU Head', label: 'BU Head', required: true, type: 'string', example: 'Sunil Jha' },
            { name: 'Project', label: 'Project', required: true, type: 'string', example: 'NYU - AR' },
            { name: 'Work Location', label: 'Work Location', required: true, type: 'string', example: 'Noida' },
            { name: 'Contact No', label: 'Contact No', required: true, type: 'string', example: '9876543210' },
            { name: 'Email', label: 'Email', required: true, type: 'string', example: 'john@example.com' },
            { name: 'Address', label: 'Address', required: true, type: 'string', example: 'Noida, UP' },
            { name: 'Recruiter', label: 'Recruiter', required: true, type: 'string', example: 'Priya Singh' },
            { name: 'Source', label: 'Source', required: true, type: 'string', example: 'Naukri' },
            { name: 'Sub Source', label: 'Sub Source', required: true, type: 'string', example: 'Direct' },
            // NON-MANDATORY
            { name: 'Assessment Score', label: 'Assessment Score', required: false, type: 'string', example: '85' },
            { name: 'V&A interviewer', label: 'V&A interviewer', required: false, type: 'string', example: 'Amit Kumar' },
            // MANDATORY
            { name: 'Ops Interviewer', label: 'Ops Interviewer', required: true, type: 'string', example: 'Rakesh Gupta' },
            // NON-MANDATORY
            { name: 'Offer Status', label: 'Offer Status', required: false, type: 'string', example: 'Offered' },
            { name: 'HB / PB', label: 'HB / PB', required: false, type: 'string', example: 'HB' },
            { name: 'Host System', label: 'Host System', required: false, type: 'string', example: 'EPIC' },
            { name: 'SPOC', label: 'SPOC', required: false, type: 'string', example: 'Deepak' },
            { name: 'UAN Check', label: 'UAN Check', required: false, type: 'string', example: 'Done' },
            { name: 'UAN - Date', label: 'UAN - Date', required: false, type: 'string', example: '10-01-2025' },
            { name: 'Last Company/Current Company', label: 'Last Company/Current Company', required: false, type: 'string', example: 'ABC Corp' },
            // MANDATORY
            { name: 'Re-hire', label: 'Re-hire', required: true, type: 'string', example: 'No' },
            // NON-MANDATORY
            { name: 'WFH/WFO', label: 'WFH/WFO', required: false, type: 'string', example: 'WFO' },
            { name: 'Evergreen Requisition', label: 'Evergreen Requisition', required: false, type: 'string', example: 'JR104117' },
            // MANDATORY
            { name: 'Actual Requisition', label: 'Actual Requisition', required: true, type: 'string', example: 'JR104120' }
          ]}
          onUpload={handleBulkUpload}
          onClose={() => setShowBulkUploadModal(false)}
        />
      )}

      {/* Coordinator Assignments Modal - For HR Head / Super Admin */}
      {showAssignmentsModal && (
        <div className="modal-overlay" onClick={() => setShowAssignmentsModal(false)}>
          <div className="modal-content modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '85vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3><MdSupervisorAccount style={{ marginRight: '8px' }} /> Coordinator Assignments</h3>
              <button className="close-btn" onClick={() => setShowAssignmentsModal(false)}>
                <MdClose />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              {isLoadingAssignments ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="spinner"></div>
                  <p>Loading assignments...</p>
                </div>
              ) : coordinatorAssignments ? (
                <div>
                  {/* Summary Stats */}
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                    <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '8px', flex: '1', minWidth: '150px' }}>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Total Assigned</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0ea5e9' }}>{coordinatorAssignments.totalCandidatesAssigned || 0}</div>
                    </div>
                    <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '8px', flex: '1', minWidth: '150px' }}>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Coordinators</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#22c55e' }}>{coordinatorAssignments.totalCoordinators || 0}</div>
                    </div>
                  </div>

                  {/* Coordinator List */}
                  {coordinatorAssignments.coordinators && coordinatorAssignments.coordinators.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {coordinatorAssignments.coordinators.map((coord, idx) => (
                        <div key={idx} style={{ background: '#f8fafc', borderRadius: '8px', padding: '1rem', border: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '1rem', color: '#1e293b' }}>{coord.coordinatorName}</div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{coord.coordinatorEmail}</div>
                            </div>
                            <div style={{ background: '#6366f1', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>
                              {coord.totalAssigned} assigned
                            </div>
                          </div>

                          {/* Stage breakdown */}
                          {coord.byStage && Object.keys(coord.byStage).length > 0 && (
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                              {Object.entries(coord.byStage).map(([stage, count]) => (
                                <span key={stage} style={{
                                  background: '#e2e8f0',
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  color: '#475569'
                                }}>
                                  {getStageLabel(stage)}: {count}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Recent assignments */}
                          {coord.recentAssignments && coord.recentAssignments.length > 0 && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.375rem', fontWeight: '500' }}>Recent Assignments:</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                                {coord.recentAssignments.map((assignment, aIdx) => (
                                  <div key={aIdx} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: 'white',
                                    padding: '0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem'
                                  }}>
                                    <div>
                                      <span style={{ fontWeight: '500' }}>{assignment.candidateName}</span>
                                      <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>({assignment.candidateCode})</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                      {assignment.interviewDate && (
                                        <span style={{ color: '#6366f1' }}>
                                          <MdSchedule style={{ marginRight: '2px', verticalAlign: 'middle' }} />
                                          {new Date(assignment.interviewDate).toLocaleDateString()}
                                        </span>
                                      )}
                                      <span className={`stage-badge stage-${getStageClass(assignment.stage)}`} style={{ fontSize: '0.65rem', padding: '0.125rem 0.375rem' }}>
                                        {getStageLabel(assignment.stage)}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                      <MdSupervisorAccount style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '0.5rem' }} />
                      <p>No coordinator assignments yet</p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  <p>No data available</p>
                </div>
              )}
            </div>
            <div className="form-actions" style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' }}>
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowAssignmentsModal(false)}>Close</button>
              <button type="button" className="admin-btn admin-btn-primary" onClick={loadCoordinatorAssignments}>
                <MdRefresh style={{ marginRight: '4px' }} /> Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CandidatePipeline;
